import React, { useId, useState } from "react";
import { Link } from "react-router-dom";

/* =========================
   TOKENS & UTILITIES
   ========================= */
const TokenStyles = () => (
  <style>{`
  :root{
    /* Palette */
    --ink: #111827; 
    --ink-soft: #4B5563;
    --bg: #FAFAF9;
    --card: #FFFFFF;
    --line: #E5E7EB;
    
    /* Accents */
    --accent-blue: #0056D2;  
    --accent-teal: #0D9488;  
    --accent-purple: #7C3AED; 
    
    /* Visuals */
    --sh-sm: 0 1px 3px rgba(0,0,0,0.05);
    --sh-md: 0 10px 30px -10px rgba(0,0,0,0.08);
    --font-serif: Georgia, Cambria, "Times New Roman", Times, serif;
    --font-sans: system-ui, -apple-system, sans-serif;
  }

  body { background: var(--bg); color: var(--ink); margin: 0; font-family: var(--font-sans); -webkit-font-smoothing: antialiased; }
  .container { max-width: 64rem; margin: 0 auto; padding: 0 2rem; }

  /* Typography */
  h1, h2, h3 { font-family: var(--font-serif); }
  .text-ink { color: var(--ink); }
  .text-soft { color: var(--ink-soft); }
  
  /* Utilities */
  .bg-card { background: var(--card); }
  .border { border: 1px solid var(--line); }
  .rounded-xl { border-radius: 12px; }
  .shadow-md { box-shadow: var(--sh-md); }
  .flex { display: flex; }
  .gap-4 { gap: 1.5rem; }
  .items-center { align-items: center; }
  .justify-between { justify-content: space-between; }
  .grid { display: grid; }
  .w-full { width: 100%; }
  .list-disc { list-style-type: disc; padding-left: 1.25rem; line-height: 1.6; }
  .p-6 { padding: 1.5rem; }
  
  /* Animations */
  .hover-lift { transition: transform 0.2s ease, box-shadow 0.2s ease; }
  .hover-lift:hover { transform: translateY(-2px); box-shadow: var(--sh-md); }
  .rotate-180 { transform: rotate(180deg); }
  .transition { transition: all 0.2s ease; }

  /* Sticky Header */
  .sticky-header {
    position: sticky; top: 0; z-index: 50;
    background: rgba(255,255,255,0.9);
    backdrop-filter: blur(8px);
    border-bottom: 1px solid var(--line);
  }

  /* Buttons */
  .vol-btn {
    background: none; border: none; padding: 0.5rem 1rem;
    font-family: var(--font-sans); font-size: 14px; font-weight: 600;
    color: var(--ink-soft); cursor: pointer; border-radius: 6px;
    transition: all 0.2s ease;
  }
  .vol-btn.active { background: var(--ink); color: #fff; }

  /* Infographic Styles */
  .info-label { font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; opacity: 0.6; margin-bottom: 0.25rem; display: block; }
  .phase-step { position: relative; padding-left: 1.5rem; border-left: 2px solid var(--line); padding-bottom: 1.5rem; }
  .phase-step:last-child { border-left: 2px solid transparent; }
  .phase-dot { position: absolute; left: -5px; top: 0; width: 8px; height: 8px; border-radius: 50%; background: var(--ink-soft); }

  @media (min-width: 768px) { .md\\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
`}</style>
);

/* =========================
   UI COMPONENTS
   ========================= */
const Pill = ({ children }) => (
  <span
    style={{
      background: "#F3F4F6",
      color: "#374151",
      padding: "4px 12px",
      borderRadius: "100px",
      fontSize: "11px",
      fontWeight: "600",
      letterSpacing: "0.05em",
      textTransform: "uppercase",
    }}
  >
    {children}
  </span>
);

const Card = ({ title, subtitle, accentColor, children }) => (
  <section
    className="bg-card border rounded-xl shadow-md p-6 hover-lift"
    style={{ borderTop: `4px solid ${accentColor}` }}
  >
    <header style={{ marginBottom: "1rem" }}>
      <h3
        className="text-ink text-lg font-bold"
        style={{ fontFamily: "var(--font-sans)", letterSpacing: "-0.01em" }}
      >
        {title}
      </h3>
      {subtitle && (
        <p className="text-soft text-sm" style={{ marginTop: "0.25rem" }}>
          {subtitle}
        </p>
      )}
    </header>
    {children}
  </section>
);

const Chevron = ({ open }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    className={`transition ${open ? "rotate-180" : ""}`}
    style={{ color: "#9CA3AF" }}
  >
    <path
      d="M6 8l4 4 4-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AccordionItem = ({
  id,
  question,
  answer,
  isOpen,
  onToggle,
  accentColor,
}) => {
  const panelId = `${id}-panel`;
  const btnId = `${id}-button`;

  return (
    <div
      className="bg-card rounded-xl border"
      style={{
        marginBottom: "1rem",
        overflow: "hidden",
        transition: "all 0.2s ease",
        boxShadow: isOpen
          ? "0 12px 24px -8px rgba(0,0,0,0.1)"
          : "0 1px 2px rgba(0,0,0,0.05)",
        borderColor: isOpen ? accentColor : "var(--line)",
      }}
    >
      <button
        id={btnId}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onToggle}
        className="w-full text-left p-6 flex items-start justify-between gap-4 cursor-pointer"
        style={{ background: "none", border: "none" }}
      >
        <div className="flex gap-4 items-baseline">
          <span
            style={{ color: accentColor, fontWeight: "bold", fontSize: "14px" }}
          >
            Q.
          </span>
          <span
            className="text-ink text-lg font-bold"
            style={{ fontFamily: "var(--font-serif)", lineHeight: "1.4" }}
          >
            {question}
          </span>
        </div>
        <div style={{ marginTop: "4px" }}>
          <Chevron open={isOpen} />
        </div>
      </button>

      <div
        id={panelId}
        style={{
          display: isOpen ? "block" : "none",
          borderTop: "1px solid var(--line)",
        }}
      >
        <div
          className="p-6 text-soft"
          style={{ lineHeight: "1.7", fontSize: "16px", paddingLeft: "3.5rem" }}
        >
          {answer}
        </div>
      </div>
    </div>
  );
};

/* =========================
   VISUAL DATA: SALES ONBOARDING
   ========================= */
const SALES_DATA = [
  {
    id: "s1",
    q: "Overarching Strategy: What are the key elements of a fully developed, successful program?",
    a: (
      <div>
        <div
          style={{
            background: "linear-gradient(to right, #EFF6FF, #ffffff)",
            padding: "1.5rem",
            borderRadius: "12px",
            borderLeft: "4px solid var(--accent-blue)",
            marginBottom: "1.5rem",
          }}
        >
          <span className="info-label" style={{ color: "var(--accent-blue)" }}>
            The North Star
          </span>
          <p
            style={{
              fontSize: "1.25rem",
              lineHeight: "1.4",
              fontWeight: "500",
              color: "var(--ink)",
              margin: 0,
            }}
          >
            Success is not training completion. Success is{" "}
            <strong style={{ color: "var(--accent-blue)" }}>
              Certified Autonomy
            </strong>
            .
          </p>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "1rem",
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "1rem",
              borderRadius: "8px",
              border: "1px solid var(--line)",
            }}
          >
            <strong
              style={{
                color: "var(--ink)",
                display: "block",
                marginBottom: "0.5rem",
              }}
            >
              1. Role Tracks
            </strong>
            <p style={{ fontSize: "0.9rem", margin: 0 }}>
              Distinct paths for SDR vs. AE. No "peanut butter" training.
            </p>
          </div>
          <div
            style={{
              background: "#fff",
              padding: "1rem",
              borderRadius: "8px",
              border: "1px solid var(--line)",
            }}
          >
            <strong
              style={{
                color: "var(--ink)",
                display: "block",
                marginBottom: "0.5rem",
              }}
            >
              2. Integrated Sages
            </strong>
            <p style={{ fontSize: "0.9rem", margin: 0 }}>
              MEDDICC & Challenger woven into daily work, not just theory.
            </p>
          </div>
          <div
            style={{
              background: "#fff",
              padding: "1rem",
              borderRadius: "8px",
              border: "1px solid var(--line)",
            }}
          >
            <strong
              style={{
                color: "var(--ink)",
                display: "block",
                marginBottom: "0.5rem",
              }}
            >
              3. The Gate System
            </strong>
            <div style={{ fontSize: "0.8rem", color: "var(--ink-soft)" }}>
              L1 Knowledge → L2 Scenario → L3 Live Fire
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: "s2",
    q: "What does each phase look like from a content perspective?",
    a: (
      <div
        style={{
          background: "#F9FAFB",
          padding: "1.5rem",
          borderRadius: "12px",
          border: "1px solid var(--line)",
        }}
      >
        <div className="phase-step">
          <div
            className="phase-dot"
            style={{ background: "var(--accent-blue)" }}
          />
          <strong style={{ color: "var(--ink)" }}>Phase 1: The Transfer</strong>
          <p style={{ margin: "0.25rem 0 0", fontSize: "0.9rem" }}>
            Migration of legacy assets to LMS. Establishing baseline role
            definitions.
          </p>
        </div>
        <div className="phase-step">
          <div
            className="phase-dot"
            style={{ background: "var(--accent-blue)" }}
          />
          <strong style={{ color: "var(--ink)" }}>
            Phase 2: Contextualization
          </strong>
          <p style={{ margin: "0.25rem 0 0", fontSize: "0.9rem" }}>
            Injecting Geo-specific nuance. Building the buyer persona library.
          </p>
        </div>
        <div className="phase-step">
          <div
            className="phase-dot"
            style={{ background: "var(--accent-blue)" }}
          />
          <strong style={{ color: "var(--ink)" }}>Phase 3: Deep Mastery</strong>
          <p style={{ margin: "0.25rem 0 0", fontSize: "0.9rem" }}>
            Asynchronous modules. Heavy focus on product differentiation.
          </p>
        </div>
        <div
          className="phase-step"
          style={{ paddingBottom: 0, borderLeft: "none" }}
        >
          <div
            className="phase-dot"
            style={{ background: "var(--accent-blue)" }}
          />
          <strong style={{ color: "var(--ink)" }}>Phase 4: Live Fire</strong>
          <p style={{ margin: "0.25rem 0 0", fontSize: "0.9rem" }}>
            Role-plays. Call reviews. Manager sign-off on "The Pitch."
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "s3",
    q: "Provide a visual timeline with associated deliverables for each phase.",
    a: (
      <div style={{ display: "flex", gap: "1rem", flexDirection: "column" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            padding: "0.75rem",
            background: "#EFF6FF",
            borderRadius: "8px",
            borderLeft: "4px solid var(--accent-blue)",
          }}
        >
          <div
            style={{
              minWidth: "80px",
              fontSize: "0.8rem",
              fontWeight: "bold",
              color: "var(--accent-blue)",
            }}
          >
            DAYS 1-14
          </div>
          <div style={{ fontSize: "0.95rem", fontWeight: "500" }}>
            Transfer & Normalize
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            padding: "0.75rem",
            background: "#EFF6FF",
            borderRadius: "8px",
            borderLeft: "4px solid var(--accent-blue)",
          }}
        >
          <div
            style={{
              minWidth: "80px",
              fontSize: "0.8rem",
              fontWeight: "bold",
              color: "var(--accent-blue)",
            }}
          >
            DAYS 15-45
          </div>
          <div style={{ fontSize: "0.95rem", fontWeight: "500" }}>
            Geo Context + Role Learning
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            padding: "0.75rem",
            background: "#EFF6FF",
            borderRadius: "8px",
            borderLeft: "4px solid var(--accent-blue)",
          }}
        >
          <div
            style={{
              minWidth: "80px",
              fontSize: "0.8rem",
              fontWeight: "bold",
              color: "var(--accent-blue)",
            }}
          >
            DAYS 46-60
          </div>
          <div style={{ fontSize: "0.95rem", fontWeight: "500" }}>
            Asynchronous Mastery
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            padding: "0.75rem",
            background: "#DBEAFE",
            borderRadius: "8px",
            borderLeft: "4px solid var(--accent-blue)",
          }}
        >
          <div
            style={{
              minWidth: "80px",
              fontSize: "0.8rem",
              fontWeight: "bold",
              color: "var(--accent-blue)",
            }}
          >
            DAYS 61-90
          </div>
          <div
            style={{
              fontSize: "0.95rem",
              fontWeight: "bold",
              color: "var(--ink)",
            }}
          >
            Certified Readiness (The Graduation)
          </div>
        </div>
      </div>
    ),
  },
  {
    id: "s4",
    q: "What resources will be needed to accomplish each phase?",
    a: (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "1.5rem",
        }}
      >
        <div
          style={{
            background: "#fff",
            border: "1px dashed var(--line)",
            padding: "1.25rem",
            borderRadius: "8px",
          }}
        >
          <div
            style={{
              fontSize: "0.85rem",
              textTransform: "uppercase",
              fontWeight: "bold",
              color: "#6B7280",
              marginBottom: "0.5rem",
            }}
          >
            The Starter Squad (MVP)
          </div>
          <ul
            style={{
              margin: 0,
              paddingLeft: "1.2rem",
              fontSize: "0.95rem",
              lineHeight: "1.5",
            }}
          >
            <li>Sales Enablement Lead</li>
            <li>Leadership Sponsor</li>
            <li>Product SME (Part-time)</li>
          </ul>
        </div>
        <div
          style={{
            background: "#F0F9FF",
            border: "1px solid #BAE6FD",
            padding: "1.25rem",
            borderRadius: "8px",
          }}
        >
          <div
            style={{
              fontSize: "0.85rem",
              textTransform: "uppercase",
              fontWeight: "bold",
              color: "var(--accent-blue)",
              marginBottom: "0.5rem",
            }}
          >
            The Scale Team (Ideal)
          </div>
          <ul
            style={{
              margin: 0,
              paddingLeft: "1.2rem",
              fontSize: "0.95rem",
              lineHeight: "1.5",
            }}
          >
            <li>Instructional Designer</li>
            <li>Regional Leads</li>
            <li>Sales Ops Analyst</li>
            <li>Coaching Captains</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: "s5",
    q: "What are the risks?",
    a: (
      <div style={{ display: "grid", gap: "1rem" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            gap: "1rem",
            paddingBottom: "1rem",
            borderBottom: "1px solid var(--line)",
          }}
        >
          <div style={{ color: "#DC2626", fontWeight: "600" }}>The Threat</div>
          <div style={{ color: "#059669", fontWeight: "600" }}>The Defense</div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            gap: "1rem",
          }}
        >
          <div style={{ fontSize: "0.95rem" }}>"Checking the box"</div>
          <div style={{ fontSize: "0.95rem" }}>
            Tie certification gates to actual pipeline quality signals.
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            gap: "1rem",
          }}
        >
          <div style={{ fontSize: "0.95rem" }}>Content Decay</div>
          <div style={{ fontSize: "0.95rem" }}>
            Quarterly governance reviews with mandatory versioning.
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            gap: "1rem",
          }}
        >
          <div style={{ fontSize: "0.95rem" }}>Inconsistent Coaching</div>
          <div style={{ fontSize: "0.95rem" }}>
            Standardized rubrics + required manager sign-off.
          </div>
        </div>
      </div>
    ),
  },
];

/* =========================
   VISUAL DATA: PRODUCT EDUCATION
   ========================= */
const PRODUCT_DATA = [
  {
    id: "p1",
    q: "Overarching Strategy: What are the key elements of a fully developed, successful program?",
    a: (
      <div>
        <div
          style={{
            background: "linear-gradient(to right, #FAF5FF, #ffffff)",
            padding: "1.5rem",
            borderRadius: "12px",
            borderLeft: "4px solid var(--accent-purple)",
            marginBottom: "1.5rem",
          }}
        >
          <span
            className="info-label"
            style={{ color: "var(--accent-purple)" }}
          >
            The Core Shift
          </span>
          <p
            style={{
              fontSize: "1.25rem",
              lineHeight: "1.4",
              fontWeight: "500",
              color: "var(--ink)",
              margin: 0,
            }}
          >
            From{" "}
            <span style={{ textDecoration: "line-through", opacity: 0.5 }}>
              knowledge transfer
            </span>{" "}
            to{" "}
            <strong style={{ color: "var(--accent-purple)" }}>
              Performance Support
            </strong>{" "}
            (The 5 Moments of Need).
          </p>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "1rem",
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "1rem",
              borderRadius: "8px",
              border: "1px solid var(--line)",
              boxShadow: "var(--sh-sm)",
            }}
          >
            <strong
              style={{
                color: "var(--ink)",
                display: "block",
                marginBottom: "0.5rem",
              }}
            >
              1. The Value Chain
            </strong>
            <div style={{ fontSize: "0.85rem", color: "var(--ink-soft)" }}>
              Role Journey → Certification → Measured Impact
            </div>
          </div>
          <div
            style={{
              background: "#fff",
              padding: "1rem",
              borderRadius: "8px",
              border: "1px solid var(--line)",
              boxShadow: "var(--sh-sm)",
            }}
          >
            <strong
              style={{
                color: "var(--ink)",
                display: "block",
                marginBottom: "0.5rem",
              }}
            >
              2. Competency Tracks
            </strong>
            <div style={{ fontSize: "0.85rem", color: "var(--ink-soft)" }}>
              Generalist (Pitch) vs. Specialist (Demo) vs. Consultant (Config)
            </div>
          </div>
          <div
            style={{
              background: "#fff",
              padding: "1rem",
              borderRadius: "8px",
              border: "1px solid var(--line)",
              boxShadow: "var(--sh-sm)",
            }}
          >
            <strong
              style={{
                color: "var(--ink)",
                display: "block",
                marginBottom: "0.5rem",
              }}
            >
              3. The Cert Ladder
            </strong>
            <div style={{ fontSize: "0.85rem", color: "var(--ink-soft)" }}>
              L1 Foundations → L2 Scenario → L3 Live Mastery
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: "p2",
    q: "What does each phase look like from a content perspective?",
    a: (
      <div
        style={{
          display: "flex",
          gap: "1rem",
          alignItems: "center",
          justifyContent: "space-between",
          background: "#FAFAF9",
          padding: "1rem",
          borderRadius: "12px",
          border: "1px solid var(--line)",
        }}
      >
        <div style={{ textAlign: "center", flex: 1 }}>
          <div
            style={{
              background: "#F3E8FF",
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
              color: "var(--accent-purple)",
              margin: "0 auto 0.5rem",
            }}
          >
            1
          </div>
          <strong style={{ display: "block", fontSize: "0.9rem" }}>
            Inventory
          </strong>
          <p style={{ fontSize: "0.8rem", color: "var(--ink-soft)" }}>
            Audit & Taxonomy
          </p>
        </div>
        <div style={{ color: "var(--line)", fontSize: "1.5rem" }}>→</div>
        <div style={{ textAlign: "center", flex: 1 }}>
          <div
            style={{
              background: "#F3E8FF",
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
              color: "var(--accent-purple)",
              margin: "0 auto 0.5rem",
            }}
          >
            2
          </div>
          <strong style={{ display: "block", fontSize: "0.9rem" }}>
            Align
          </strong>
          <p style={{ fontSize: "0.8rem", color: "var(--ink-soft)" }}>
            Map to Roles
          </p>
        </div>
        <div style={{ color: "var(--line)", fontSize: "1.5rem" }}>→</div>
        <div style={{ textAlign: "center", flex: 1 }}>
          <div
            style={{
              background: "#F3E8FF",
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
              color: "var(--accent-purple)",
              margin: "0 auto 0.5rem",
            }}
          >
            3
          </div>
          <strong style={{ display: "block", fontSize: "0.9rem" }}>
            Launch
          </strong>
          <p style={{ fontSize: "0.8rem", color: "var(--ink-soft)" }}>
            Hub & Dashboard
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "p3",
    q: "Provide a visual timeline with associated deliverables for each phase.",
    a: (
      <div style={{ display: "flex", gap: "1rem", flexDirection: "column" }}>
        <div
          style={{
            padding: "1rem",
            background: "#fff",
            borderLeft: "4px solid var(--accent-purple)",
            borderRadius: "8px",
            border: "1px solid var(--line)",
            borderLeftWidth: "4px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "0.5rem",
            }}
          >
            <strong>Weeks 1-4</strong>
            <span
              style={{
                fontSize: "0.8rem",
                background: "#F3E8FF",
                padding: "2px 8px",
                borderRadius: "100px",
                color: "var(--accent-purple)",
              }}
            >
              ASSESSMENT
            </span>
          </div>
          <div style={{ fontSize: "0.9rem", color: "var(--ink-soft)" }}>
            Content Inventory & Gap Analysis
          </div>
        </div>
        <div
          style={{
            padding: "1rem",
            background: "#fff",
            borderLeft: "4px solid var(--accent-purple)",
            borderRadius: "8px",
            border: "1px solid var(--line)",
            borderLeftWidth: "4px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "0.5rem",
            }}
          >
            <strong>Weeks 5-8</strong>
            <span
              style={{
                fontSize: "0.8rem",
                background: "#F3E8FF",
                padding: "2px 8px",
                borderRadius: "100px",
                color: "var(--accent-purple)",
              }}
            >
              DESIGN
            </span>
          </div>
          <div style={{ fontSize: "0.9rem", color: "var(--ink-soft)" }}>
            Role Alignment & Path Design
          </div>
        </div>
        <div
          style={{
            padding: "1rem",
            background: "#fff",
            borderLeft: "4px solid var(--accent-purple)",
            borderRadius: "8px",
            border: "1px solid var(--line)",
            borderLeftWidth: "4px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "0.5rem",
            }}
          >
            <strong>Weeks 9-12</strong>
            <span
              style={{
                fontSize: "0.8rem",
                background: "#F3E8FF",
                padding: "2px 8px",
                borderRadius: "100px",
                color: "var(--accent-purple)",
              }}
            >
              EXECUTION
            </span>
          </div>
          <div style={{ fontSize: "0.9rem", color: "var(--ink-soft)" }}>
            Launch Hub, Certifications, & Measurement
          </div>
        </div>
      </div>
    ),
  },
  {
    id: "p4",
    q: "What resources will be needed to accomplish each phase?",
    a: (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "1.5rem",
        }}
      >
        <div
          style={{
            background: "#fff",
            border: "1px dashed var(--line)",
            padding: "1.25rem",
            borderRadius: "8px",
          }}
        >
          <div
            style={{
              fontSize: "0.85rem",
              textTransform: "uppercase",
              fontWeight: "bold",
              color: "#6B7280",
              marginBottom: "0.5rem",
            }}
          >
            The Starter Squad (MVP)
          </div>
          <ul
            style={{
              margin: 0,
              paddingLeft: "1.2rem",
              fontSize: "0.95rem",
              lineHeight: "1.5",
            }}
          >
            <li>Product Education Lead</li>
            <li>Product Managers</li>
            <li>Enablement Partner</li>
          </ul>
        </div>
        <div
          style={{
            background: "#FAF5FF",
            border: "1px solid #E9D5FF",
            padding: "1.25rem",
            borderRadius: "8px",
          }}
        >
          <div
            style={{
              fontSize: "0.85rem",
              textTransform: "uppercase",
              fontWeight: "bold",
              color: "var(--accent-purple)",
              marginBottom: "0.5rem",
            }}
          >
            The Scale Team (Ideal)
          </div>
          <ul
            style={{
              margin: 0,
              paddingLeft: "1.2rem",
              fontSize: "0.95rem",
              lineHeight: "1.5",
            }}
          >
            <li>Instructional Designer</li>
            <li>Analytics Owner</li>
            <li>Regional Content SMEs</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: "p5",
    q: "What are the risks?",
    a: (
      <div style={{ display: "grid", gap: "1rem" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            gap: "1rem",
            paddingBottom: "1rem",
            borderBottom: "1px solid var(--line)",
          }}
        >
          <div style={{ color: "#DC2626", fontWeight: "600" }}>The Threat</div>
          <div style={{ color: "#7C3AED", fontWeight: "600" }}>
            The Mitigation
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            gap: "1rem",
          }}
        >
          <div style={{ fontSize: "0.95rem" }}>Feature-Heavy Bloat</div>
          <div style={{ fontSize: "0.95rem" }}>
            Scenario-based design (Action Mapping).
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            gap: "1rem",
          }}
        >
          <div style={{ fontSize: "0.95rem" }}>Low Adoption</div>
          <div style={{ fontSize: "0.95rem" }}>
            Certification-gated access to systems.
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            gap: "1rem",
          }}
        >
          <div style={{ fontSize: "0.95rem" }}>Measurement Blind Spots</div>
          <div style={{ fontSize: "0.95rem" }}>
            Link usage data directly to revenue/renewal metrics.
          </div>
        </div>
      </div>
    ),
  },
];

/* =========================
   MAIN PAGE COMPONENT
   ========================= */
export default function DesignIntentPage() {
  const [openId, setOpenId] = useState(null);
  const [volume, setVolume] = useState("sales"); // 'sales' | 'product'
  const uid = useId();

  const activeData = volume === "sales" ? SALES_DATA : PRODUCT_DATA;
  const activeColor =
    volume === "sales" ? "var(--accent-blue)" : "var(--accent-purple)";

  return (
    <div className="bg-bg min-h-screen pb-24">
      <TokenStyles />

      {/* Sticky Header */}
      <div className="sticky-header">
        <div
          className="container"
          style={{
            maxWidth: "64rem",
            padding: "0.75rem 2rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div className="flex items-center gap-4">
            <Link
              to="/"
              style={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <div
                style={{
                  width: "8px",
                  height: "24px",
                  background: activeColor,
                  borderRadius: "2px",
                  transition: "background 0.3s",
                }}
              />
              <strong style={{ fontSize: "14px", color: "var(--ink)" }}>
                Enablement Playbooks
              </strong>
            </Link>
            <span style={{ color: "#D1D5DB" }}>/</span>
            <span style={{ fontSize: "14px", color: "var(--ink-soft)" }}>
              The Blueprint
            </span>
          </div>
          <Pill>Exec Ready</Pill>
        </div>
      </div>

      <main className="container mt-10">
        {/* Title Section */}
        <div
          className="mb-8"
          style={{
            borderBottom: "1px solid var(--line)",
            paddingBottom: "2rem",
          }}
        >
          <h1
            className="text-ink"
            style={{
              fontSize: "36px",
              lineHeight: "1.2",
              marginBottom: "16px",
              color: activeColor,
            }}
          >
            The Blueprint
          </h1>
          <p
            className="text-soft"
            style={{ fontSize: "20px", lineHeight: "1.6", maxWidth: "48rem" }}
          >
            Mapping original requirements to the delivered frameworks. Choose a
            volume below to review the solution logic.
          </p>

          {/* Volume Switcher */}
          <div className="flex gap-2 mt-6">
            <button
              className={`vol-btn ${volume === "sales" ? "active" : ""}`}
              onClick={() => {
                setVolume("sales");
                setOpenId(null);
              }}
            >
              Volume I: Sales Onboarding
            </button>
            <button
              className={`vol-btn ${volume === "product" ? "active" : ""}`}
              onClick={() => {
                setVolume("product");
                setOpenId(null);
              }}
            >
              Volume II: Product Education
            </button>
          </div>
        </div>

        {/* Objectives / Guiding Questions */}
        <section className="grid md:grid-cols-2 gap-4 mb-12">
          <Card
            title="Objectives"
            subtitle="What this framework achieves"
            accentColor={activeColor}
          >
            <ul className="list-disc text-sm text-soft">
              {volume === "sales" ? (
                <>
                  <li className="mb-2">
                    Accelerate time-to-pipeline for new hires.
                  </li>
                  <li className="mb-2">
                    Standardize "what good looks like" globally.
                  </li>
                  <li className="mb-2">
                    Validate readiness through certified gates.
                  </li>
                </>
              ) : (
                <>
                  <li className="mb-2">
                    Shift from knowledge transfer to performance support.
                  </li>
                  <li className="mb-2">
                    Ensure teams can apply product knowledge in real scenarios.
                  </li>
                  <li className="mb-2">
                    Measure impact on adoption and support deflection.
                  </li>
                </>
              )}
            </ul>
          </Card>
          <Card
            title="Guiding Questions"
            subtitle="What we solved for"
            accentColor="var(--accent-teal)"
          >
            <ul className="list-disc text-sm text-soft">
              <li className="mb-2">What are the key elements of success?</li>
              <li className="mb-2">How does content map to phases?</li>
              <li className="mb-2">Who is required to execute?</li>
              <li className="mb-2">What are the risks and mitigations?</li>
            </ul>
          </Card>
        </section>

        {/* Q&A Accordion */}
        <section>
          {activeData.map((item) => (
            <AccordionItem
              key={`${uid}-${item.id}`}
              id={`${uid}-${item.id}`}
              question={item.q}
              answer={item.a}
              isOpen={openId === item.id}
              onToggle={() => setOpenId(openId === item.id ? null : item.id)}
              accentColor={activeColor}
            />
          ))}
        </section>
      </main>
    </div>
  );
}
